import { msg } from "@lingui/core/macro";
import { Show } from "solid-js";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useI18n } from "@/providers/i18n-provider";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useDrizzle } from "@/hooks/use-drizzle";
import { createPowerSyncQuery } from "@stockpilot/shared/solid";
import {
  cn,
  getProductsQuery,
  getStockQuantsQuery,
  getWarehousesQuery,
  queryKeys,
} from "@stockpilot/shared";
import type { ProductRecord, StockQuantRecord, WarehouseRecord } from "@stockpilot/powersync";
import { BarChart3 } from "lucide-solid";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { COMPACT_THRESHOLD, formatCompactNumber } from "@/utils/number";

interface StatsSectionProps {
  /** Organization scope (equals userId in the personal F1 scope). */
  userId: string;
}

export function StatsSection(props: StatsSectionProps) {
  const { db } = usePowerSyncContext();
  const drizzle = useDrizzle();
  const { t } = useI18n();

  const enabled = () => Boolean(drizzle() && props.userId);

  const products = createPowerSyncQuery<ProductRecord>({
    queryKey: () => queryKeys.inventory.products(props.userId),
    db: () => db(),
    queryFn: () => {
      const dbInstance = drizzle();
      return dbInstance ? getProductsQuery(dbInstance, props.userId) : null;
    },
    enabled,
    throttleTime: 300,
    suspense: false,
  });

  const warehouses = createPowerSyncQuery<WarehouseRecord>({
    queryKey: () => queryKeys.inventory.warehouses(props.userId),
    db: () => db(),
    queryFn: () => {
      const dbInstance = drizzle();
      return dbInstance ? getWarehousesQuery(dbInstance, props.userId) : null;
    },
    enabled,
    throttleTime: 300,
    suspense: false,
  });

  const quants = createPowerSyncQuery<StockQuantRecord>({
    queryKey: () => queryKeys.inventory.quants(props.userId),
    db: () => db(),
    queryFn: () => {
      const dbInstance = drizzle();
      return dbInstance ? getStockQuantsQuery(dbInstance, props.userId) : null;
    },
    enabled,
    throttleTime: 300,
    suspense: false,
  });

  const productCount = () => (products.data ?? []).length;
  const warehouseCount = () => (warehouses.data ?? []).length;
  const onHandUnits = () => (quants.data ?? []).reduce((sum, q) => sum + (q.qty ?? 0), 0);

  return (
    <Card>
      <CardHeader>
        <div class="flex items-center gap-2">
          <BarChart3 class="h-5 w-5 text-primary" />
          <CardTitle>{t(msg`Statistics`)}</CardTitle>
        </div>
      </CardHeader>
      <CardContent class="space-y-6">
        <div class="grid grid-cols-2 gap-4 sm:grid-cols-3">
          <StatCard
            label={t(msg`Products`)}
            value={productCount()}
            colorClass="text-primary"
            bgClass="bg-primary/10"
            isLoading={products.isPending}
          />
          <StatCard
            label={t(msg`Warehouses`)}
            value={warehouseCount()}
            colorClass="text-foreground"
            bgClass="bg-muted/50"
            isLoading={warehouses.isPending}
          />
          <StatCard
            label={t(msg`Units on hand`)}
            value={onHandUnits()}
            colorClass="text-success"
            bgClass="bg-success/10"
            isLoading={quants.isPending}
          />
        </div>
      </CardContent>
    </Card>
  );
}

function StatCard(props: {
  label: string;
  value: number;
  colorClass: string;
  bgClass: string;
  isLoading?: boolean;
}) {
  const { locale } = useI18n();
  const isCompact = () => props.value >= COMPACT_THRESHOLD;
  const formattedValue = () => formatCompactNumber(props.value, locale());
  const fullLabel = () => `${props.value.toLocaleString(locale())} ${props.label.toLowerCase()}`;

  return (
    <div
      class={cn(
        "flex flex-col items-center justify-center rounded-2xl p-4 text-center transition-transform duration-200 ease-out will-change-transform hover:scale-[1.02]",
        props.bgClass,
      )}
    >
      <Show
        when={props.isLoading}
        fallback={
          <Show
            when={isCompact()}
            fallback={
              <span
                class={cn(
                  "text-2xl font-black leading-none tracking-tight select-none",
                  props.colorClass,
                )}
                aria-label={fullLabel()}
              >
                {props.value}
              </span>
            }
          >
            <Tooltip openDelay={350} closeDelay={100}>
              <TooltipTrigger class="focus-visible:outline-none select-none cursor-help">
                <span
                  class={cn(
                    "text-2xl font-black leading-none tracking-tight border-b border-dashed border-current pb-0.5",
                    props.colorClass,
                  )}
                  aria-label={fullLabel()}
                >
                  {formattedValue()}
                </span>
              </TooltipTrigger>
              <TooltipContent class="max-w-[200px] text-center font-semibold bg-background text-foreground border border-border/40 shadow-lg px-2.5 py-1.5 rounded-lg select-none">
                {fullLabel()}
              </TooltipContent>
            </Tooltip>
          </Show>
        }
      >
        <Skeleton class="h-7 w-8" />
      </Show>
      <div class="mt-2 text-[10px] font-bold text-muted-foreground uppercase tracking-widest opacity-80 select-none">
        {props.label}
      </div>
    </div>
  );
}
